import React, { useState, useEffect } from 'react';
import AppIcon from '../../../../components/AppIcon';
import toast from 'react-hot-toast';

interface AutomationTask {
  name: string;
  timeSpent: string;
  automationMethod: string;
  complexity: 'Low' | 'Medium' | 'High';
  impact: string;
  tools: string[];
}

interface AnalysisData {
  automationPercentage: number;
  summary: string;
  tasks: AutomationTask[];
  protectedWork: string[];
}

interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  overallScore?: number;
  strengths?: string[];
  growthAreas?: string[];
}

interface AutomationPotentialPanelProps {
  employee: Employee | null;
  onAnalysisComplete?: (pct: number) => void;
}

const DUMMY_DATA: Record<string, AnalysisData> = {
  emp_001: {
    automationPercentage: 38,
    summary: 'About 38% of Sarah\'s current tasks involve repetitive design ops and documentation that can be automated with modern tooling. Her core creative and research work remains deeply human-centric.',
    tasks: [
      {
        name: 'Design Asset Export & Handoff',
        timeSpent: '5h/week',
        automationMethod: 'Figma plugins + Zeplin auto-sync to generate specs and export assets automatically on publish.',
        complexity: 'Low',
        impact: 'Saves ~5 hours per week, eliminates manual export errors, and speeds up developer handoff by 60%.',
        tools: ['Figma', 'Zeplin', 'Storybook'],
      },
      {
        name: 'Design QA & Accessibility Checks',
        timeSpent: '3h/week',
        automationMethod: 'Automated accessibility linting via Axe DevTools and Figma Accessibility Checker plugin.',
        complexity: 'Low',
        impact: 'Reduces QA cycle time by 70% and ensures WCAG compliance without manual review.',
        tools: ['Axe DevTools', 'Stark', 'Figma'],
      },
      {
        name: 'Weekly Status Reporting',
        timeSpent: '2h/week',
        automationMethod: 'Notion AI + Jira integration to auto-generate weekly design progress summaries.',
        complexity: 'Medium',
        impact: 'Reclaims 2 hours weekly for deep design work and improves report consistency.',
        tools: ['Notion AI', 'Jira', 'Slack'],
      },
      {
        name: 'User Research Transcription & Tagging',
        timeSpent: '4h/week',
        automationMethod: 'Otter.ai for transcription + Dovetail AI for automatic theme tagging from interview recordings.',
        complexity: 'Medium',
        impact: 'Cuts research synthesis time by 50%, enabling faster insight delivery to product teams.',
        tools: ['Otter.ai', 'Dovetail', 'Miro'],
      },
    ],
    protectedWork: [
      'Creative ideation and concept development for new product features',
      'Empathy-driven user research facilitation and relationship building',
      'Cross-functional design critique sessions and stakeholder alignment',
      'Design system governance and strategic visual direction decisions',
    ],
  },
  emp_002: {
    automationPercentage: 52,
    summary: 'Over half of Mike\'s routine engineering tasks — including testing, deployments, and code reviews — can be significantly automated. His architectural thinking and complex problem-solving remain irreplaceable.',
    tasks: [
      {
        name: 'Unit & Integration Test Writing',
        timeSpent: '6h/week',
        automationMethod: 'GitHub Copilot + Diffblue Cover to auto-generate unit tests from existing code.',
        complexity: 'Medium',
        impact: 'Reduces test authoring time by 65% while maintaining coverage standards.',
        tools: ['GitHub Copilot', 'Diffblue Cover', 'Jest'],
      },
      {
        name: 'CI/CD Pipeline Monitoring',
        timeSpent: '3h/week',
        automationMethod: 'PagerDuty + Datadog automated alerting with self-healing runbooks for common failures.',
        complexity: 'Low',
        impact: 'Eliminates manual pipeline babysitting, reducing on-call burden by 40%.',
        tools: ['Datadog', 'PagerDuty', 'GitHub Actions'],
      },
      {
        name: 'Code Review Boilerplate Checks',
        timeSpent: '4h/week',
        automationMethod: 'SonarQube + CodeClimate for automated style, security, and complexity checks before human review.',
        complexity: 'Low',
        impact: 'Saves 4 hours weekly and ensures consistent code quality standards across the team.',
        tools: ['SonarQube', 'CodeClimate', 'ESLint'],
      },
      {
        name: 'Dependency & Security Updates',
        timeSpent: '2h/week',
        automationMethod: 'Dependabot + Snyk automated PRs for dependency updates with security scanning.',
        complexity: 'Low',
        impact: 'Keeps dependencies current with zero manual effort, reducing security vulnerabilities by 80%.',
        tools: ['Dependabot', 'Snyk', 'Renovate'],
      },
      {
        name: 'Sprint Progress Reporting',
        timeSpent: '2h/week',
        automationMethod: 'Jira automation rules to generate sprint reports and velocity charts automatically.',
        complexity: 'Low',
        impact: 'Eliminates manual report compilation, freeing time for technical deep work.',
        tools: ['Jira', 'Confluence', 'Slack'],
      },
    ],
    protectedWork: [
      'System architecture design and technical decision-making',
      'Complex debugging of distributed systems and performance bottlenecks',
      'Mentoring junior engineers and conducting technical interviews',
      'Cross-team API contract negotiations and integration design',
    ],
  },
  emp_003: {
    automationPercentage: 45,
    summary: 'Nearly half of Lisa\'s data work involves routine ETL processes and standard report generation that can be automated. Her analytical interpretation and business storytelling remain high-value human skills.',
    tasks: [
      {
        name: 'Daily/Weekly Data Pipeline Runs',
        timeSpent: '5h/week',
        automationMethod: 'Apache Airflow DAGs with dbt for fully automated data transformation and loading schedules.',
        complexity: 'Medium',
        impact: 'Eliminates manual pipeline execution, ensuring data freshness with zero human intervention.',
        tools: ['Apache Airflow', 'dbt', 'Snowflake'],
      },
      {
        name: 'Standard KPI Dashboard Refresh',
        timeSpent: '3h/week',
        automationMethod: 'Tableau + Looker scheduled refreshes with automated anomaly detection alerts.',
        complexity: 'Low',
        impact: 'Dashboards update automatically, freeing Lisa for deeper analytical work.',
        tools: ['Tableau', 'Looker', 'Slack'],
      },
      {
        name: 'Data Quality Validation',
        timeSpent: '4h/week',
        automationMethod: 'Great Expectations framework for automated data quality tests on every pipeline run.',
        complexity: 'Medium',
        impact: 'Catches data issues instantly without manual spot-checking, improving data trust by 90%.',
        tools: ['Great Expectations', 'Monte Carlo', 'dbt'],
      },
      {
        name: 'Ad-hoc Report Generation',
        timeSpent: '3h/week',
        automationMethod: 'Self-service BI tools (Metabase) with natural language query for stakeholder self-service.',
        complexity: 'Medium',
        impact: 'Reduces ad-hoc requests by 60% as stakeholders can generate their own reports.',
        tools: ['Metabase', 'Looker', 'Google Sheets'],
      },
    ],
    protectedWork: [
      'Interpreting data anomalies and providing business context for unexpected trends',
      'Designing new analytical frameworks and measurement strategies',
      'Presenting insights to leadership and influencing strategic decisions',
      'Collaborating with business teams to define the right questions to ask',
    ],
  },
  emp_004: {
    automationPercentage: 41,
    summary: 'About 41% of Robert\'s marketing activities — particularly content scheduling, performance reporting, and lead nurturing — can be automated. His strategic campaign thinking and brand voice remain distinctly human.',
    tasks: [
      {
        name: 'Social Media Scheduling & Publishing',
        timeSpent: '4h/week',
        automationMethod: 'Buffer + Hootsuite AI scheduling with optimal time prediction and auto-publishing.',
        complexity: 'Low',
        impact: 'Saves 4 hours weekly and improves post timing by 35% through AI-optimized scheduling.',
        tools: ['Buffer', 'Hootsuite', 'Canva'],
      },
      {
        name: 'Campaign Performance Reporting',
        timeSpent: '3h/week',
        automationMethod: 'Google Analytics 4 + Supermetrics automated weekly reports delivered to Slack/email.',
        complexity: 'Low',
        impact: 'Eliminates manual data pulling, delivering consistent reports with zero effort.',
        tools: ['Google Analytics 4', 'Supermetrics', 'Looker Studio'],
      },
      {
        name: 'Email Lead Nurture Sequences',
        timeSpent: '5h/week',
        automationMethod: 'HubSpot workflow automation with behavioral triggers for personalized nurture sequences.',
        complexity: 'Medium',
        impact: 'Increases lead conversion by 28% through timely, behavior-based email delivery.',
        tools: ['HubSpot', 'Mailchimp', 'Salesforce'],
      },
      {
        name: 'Competitor Monitoring',
        timeSpent: '2h/week',
        automationMethod: 'Crayon + Google Alerts automated competitive intelligence digests delivered weekly.',
        complexity: 'Low',
        impact: 'Provides comprehensive competitive coverage with 80% less manual research time.',
        tools: ['Crayon', 'Semrush', 'Google Alerts'],
      },
    ],
    protectedWork: [
      'Brand strategy development and creative campaign ideation',
      'Building and nurturing key media and partner relationships',
      'Interpreting market trends and translating them into positioning strategy',
      'Leading cross-functional go-to-market planning and execution',
    ],
  },
};

const DEFAULT_DATA: AnalysisData = {
  automationPercentage: 35,
  summary: 'Approximately 35% of this employee\'s tasks involve repetitive processes that can be streamlined through automation tools. Their core judgment-based and relationship-driven work remains distinctly human.',
  tasks: [
    {
      name: 'Routine Status Updates & Reporting',
      timeSpent: '3h/week',
      automationMethod: 'Slack bots + project management integrations to auto-generate and distribute status updates.',
      complexity: 'Low',
      impact: 'Reclaims 3 hours weekly for higher-value strategic work.',
      tools: ['Slack', 'Zapier', 'Notion'],
    },
    {
      name: 'Meeting Scheduling & Coordination',
      timeSpent: '2h/week',
      automationMethod: 'Calendly + AI scheduling assistants to handle meeting coordination autonomously.',
      complexity: 'Low',
      impact: 'Eliminates back-and-forth scheduling, saving 2 hours per week.',
      tools: ['Calendly', 'Reclaim.ai', 'Google Calendar'],
    },
    {
      name: 'Document Formatting & Templates',
      timeSpent: '2h/week',
      automationMethod: 'Document automation tools with smart templates and AI-assisted formatting.',
      complexity: 'Low',
      impact: 'Reduces document prep time by 70% with consistent output quality.',
      tools: ['Notion AI', 'Google Docs', 'Coda'],
    },
  ],
  protectedWork: [
    'Strategic decision-making and complex problem-solving',
    'Building and maintaining key stakeholder relationships',
    'Creative thinking and innovation in core work areas',
  ],
};

const AutomationPotentialPanel: React.FC<AutomationPotentialPanelProps> = ({ employee, onAnalysisComplete }) => {
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandedTask, setExpandedTask] = useState<number | null>(null);

  // Load dummy data when employee changes
  useEffect(() => {
    if (employee) {
      const data = DUMMY_DATA[employee.id] || DEFAULT_DATA;
      setAnalysisData(data);
      setExpandedTask(null);
    } else {
      setAnalysisData(null);
    }
  }, [employee?.id]);

  const handleAnalyze = () => {
    if (!employee) return;
    setIsAnalyzing(true);
    setAnalysisData(null);

    // Simulate AI analysis with mock data
    setTimeout(() => {
      const data = DUMMY_DATA[employee.id] || DEFAULT_DATA;
      setAnalysisData(data);
      setIsAnalyzing(false);
      onAnalysisComplete?.(data.automationPercentage);
      toast.success('Automation analysis complete');
    }, 1500);
  };

  const complexityColor: Record<string, string> = {
    Low: 'bg-emerald-100 text-emerald-700',
    Medium: 'bg-amber-100 text-amber-700',
    High: 'bg-rose-100 text-rose-700',
  };

  const getAutomationColor = (pct: number) => {
    if (pct < 30) return { bar: 'bg-emerald-500', text: 'text-emerald-600', label: 'Low Risk' };
    if (pct < 50) return { bar: 'bg-amber-500', text: 'text-amber-600', label: 'Moderate' };
    return { bar: 'bg-rose-500', text: 'text-rose-600', label: 'High Potential' };
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
            <AppIcon name="Cpu" size={18} color="white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base">Automation Potential Analysis</h3>
            <p className="text-xs text-muted-foreground">AI-powered task automation assessment</p>
          </div>
        </div>
        <button
          onClick={handleAnalyze}
          disabled={!employee || isAnalyzing}
          className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium px-4 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAnalyzing ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <AppIcon name="Sparkles" size={14} />
              Re-analyze with AI
            </>
          )}
        </button>
      </div>

      {!employee && (
        <div className="text-center py-10 text-muted-foreground">
          <AppIcon name="UserSearch" size={36} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Select an employee to analyze automation potential</p>
        </div>
      )}

      {isAnalyzing && (
        <div className="text-center py-10">
          <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
            <AppIcon name="Cpu" size={28} color="#f59e0b" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">Analyzing work patterns...</p>
          <p className="text-xs text-muted-foreground">AI is evaluating tasks, tools, and automation opportunities</p>
        </div>
      )}

      {analysisData && !isAnalyzing && (
        <div className="space-y-5">
          {/* Automation % Gauge */}
          {(() => {
            const colors = getAutomationColor(analysisData.automationPercentage);
            return (
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-foreground">Automatable Work</span>
                  <span className={`text-2xl font-bold ${colors.text}`}>{analysisData.automationPercentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3 mb-2">
                  <div
                    className={`h-3 rounded-full transition-all duration-700 ${colors.bar}`}
                    style={{ width: `${analysisData.automationPercentage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{analysisData.summary}</p>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ml-3 flex-shrink-0 ${
                    analysisData.automationPercentage < 30 ? 'bg-emerald-100 text-emerald-700' :
                    analysisData.automationPercentage < 50 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                  }`}>{colors.label}</span>
                </div>
              </div>
            );
          })()}

          {/* Task Breakdown */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Automatable Tasks</p>
            <div className="space-y-2">
              {analysisData.tasks?.map((task, i) => (
                <div key={i} className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedTask(expandedTask === i ? null : i)}
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-amber-100 rounded-md flex items-center justify-center flex-shrink-0">
                        <AppIcon name="Zap" size={13} color="#f59e0b" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{task.name}</p>
                        <p className="text-xs text-muted-foreground">{task.timeSpent}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${complexityColor[task.complexity] || 'bg-gray-100 text-gray-600'}`}>
                        {task.complexity}
                      </span>
                      <AppIcon name={expandedTask === i ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground" />
                    </div>
                  </button>
                  {expandedTask === i && (
                    <div className="px-3 pb-3 border-t border-border bg-muted/20">
                      <div className="pt-3 space-y-2">
                        <div className="flex items-start gap-2">
                          <AppIcon name="Wrench" size={13} className="text-blue-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-foreground">How to Automate</p>
                            <p className="text-xs text-muted-foreground">{task.automationMethod}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <AppIcon name="TrendingUp" size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-foreground">Expected Impact</p>
                            <p className="text-xs text-muted-foreground">{task.impact}</p>
                          </div>
                        </div>
                        {task.tools?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {task.tools.map((tool) => (
                              <span key={tool} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{tool}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Protected Work */}
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
              <AppIcon name="Shield" size={13} /> Work That Stays Human
            </p>
            <div className="space-y-1.5">
              {analysisData.protectedWork?.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-emerald-800">
                  <AppIcon name="CheckCircle2" size={12} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationPotentialPanel;
